import { faFileText } from '@fortawesome/pro-solid-svg-icons'
import type { AttachmentType } from '~/domains/attachments/types'
import { formatName } from '~/utils/names'
import type { ApplicationInfosResponse } from '../types'

const ATTACHMENT_ID_FACTOR = 1000

type SelectedDetailState = {
  id: number | undefined
  isLoading: boolean
}

/**
 * Builds the flat attachment list backing the bulk modal viewer. File names come from the
 * lightweight summary; the heavy base64 content is merged in only for the candidate whose details
 * have been lazily loaded (`loadedDetail`, matched by real attachment id).
 *
 * Each file carries one of three states for the viewer:
 * - `content` set → render it;
 * - neither `content` nor `unavailable` → the selected candidate's detail is still loading → shimmer;
 * - `unavailable` → nothing will ever render (no storage object, or the file's content is gone) →
 *   the viewer shows an error, never an endless shimmer.
 */
export const combineBulkAttachments = (
  applications: ApplicationInfosResponse[],
  loadedDetail?: ApplicationInfosResponse,
  selectedDetail?: SelectedDetailState
): AttachmentType[] => {
  return applications.flatMap((application) => {
    const candidateName = formatName(application.applicant ?? application)
    const loadedAttachments =
      loadedDetail?.id === application.id ? loadedDetail.attachments : undefined
    const isSelectedCandidate = selectedDetail?.id === application.id
    const isLoadingContent =
      isSelectedCandidate && (selectedDetail?.isLoading ?? false)

    const attachments: AttachmentType[] = application.attachments.map(
      (attachment, index) => {
        const loadedObject = loadedAttachments?.find(
          (loaded) => loaded.id === attachment.id
        )?.object
        const hasStorageObject = (loadedObject ?? attachment.object) != null

        return {
          id: application.id * ATTACHMENT_ID_FACTOR + index,
          name:
            loadedObject?.filename ??
            attachment.object?.filename ??
            attachment.name ??
            '',
          icon: 'attachment',
          content: loadedObject?.base64Content,
          url: loadedObject?.url,
          mimeType: loadedObject?.mime,
          type: candidateName,
          unavailable:
            !loadedObject?.base64Content &&
            !isLoadingContent &&
            (!hasStorageObject || isSelectedCandidate)
        }
      }
    )

    if (application.jobmail.body && application.jobmail.body.trim() !== '') {
      attachments.push({
        id: -application.id,
        name: `Email - ${application.jobmail.subject || ''}`,
        type: candidateName,
        content: application.jobmail.body,
        formatInfo: {
          format: 'html',
          supportsZoom: false,
          icon: faFileText,
          extension: 'html'
        }
      })
    }

    return attachments
  })
}

/**
 * Resolves the application an attachment belongs to, from the synthetic id built above.
 * Email bodies use the negated application id, files use `applicationId * 1000 + index`.
 */
export const bulkAttachmentApplicationId = (attachmentId: number): number =>
  attachmentId < 0
    ? -attachmentId
    : Math.floor(attachmentId / ATTACHMENT_ID_FACTOR)
